import { defaultBusiness as NAP } from "@/lib/site-config";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Phone } from "lucide-react";
import { seoHead } from "@/lib/seo";
import { ResourceShareBlock } from "@/components/ResourceShareBlock";
import { FAQAccordion, faqSchema } from "@/components/FAQAccordion";
import { CTABlock } from "@/components/CTABlock";
import { useBusinessConfig } from "@/lib/use-business-config";

const SECTIONS: { title: string; items: string[] }[] = [
  {
    title: "1. Before you go",
    items: [
      "Check MOT history at gov.uk/check-mot-history (look for repeat advisories)",
      "Check mileage consistency across MOT records",
      "Ask for service history — stamps, invoices, digital records",
      "Quick insurance quote to confirm the car is realistic to insure",
      "Check tax and ULEZ status if relevant",
    ],
  },
  {
    title: "2. Exterior checks",
    items: [
      "Paint colour matches across panels — mismatch can mean accident repair",
      "Run a hand over panels — feel for filler or uneven gaps",
      "Tyre tread and brand match — odd-brand mixes can hint at neglect",
      "All lights, indicators and number-plate lights work",
      "No cracks or large chips in the windscreen (especially driver's view)",
    ],
  },
  {
    title: "3. Interior checks",
    items: [
      "Start cold — check ALL warning lights come on then go out",
      "Engine management, ABS, airbag and brake warning lights must go out",
      "Seatbelts retract and lock correctly",
      "Electric windows, central locking, mirrors, air-con all work",
      "Damp smell, mould or wet carpets — possible leak or flood damage",
    ],
  },
  {
    title: "4. Engine bay checks",
    items: [
      "Oil level on dipstick — clean amber, not black sludge or milky",
      "Coolant level and colour — not contaminated with oil",
      "No obvious leaks, missing covers or aftermarket bodges",
      "No burning, fuel or coolant smell after a short run",
    ],
  },
  {
    title: "5. Test drive signs",
    items: [
      "Car pulls up straight under firm braking — no pulling to one side",
      "No squealing, grinding or shudder from the brakes",
      "Clutch bite point feels normal — not high or slipping under acceleration",
      "Gearbox shifts cleanly — manual or auto",
      "Steering tracks straight with no vibration at 30–50 mph",
      "No smoke under acceleration (blue = oil, black = fuel, white = coolant)",
    ],
  },
  {
    title: "6. Questions to ask the seller",
    items: [
      "Why are you selling it?",
      "How long have you owned it?",
      "Any work coming up — clutch, timing belt, brakes, tyres?",
      "Has it been in an accident or had bodywork done?",
      "Why is the next MOT/service due soon (if it is)?",
    ],
  },
  {
    title: "7. When to walk away",
    items: [
      "Seller won't let you take it on a proper test drive",
      "No V5C logbook present (or in someone else's name)",
      "Major warning light on that 'someone will reset'",
      "Heavy oil leaks, milky oil cap, or steam from the exhaust",
      "Price suspiciously low for the year and mileage",
    ],
  },
  {
    title: "8. When to book a garage inspection",
    items: [
      "Spending £3,000+ and you're not sure",
      "Diesel/DPF concerns or short-trip history",
      "Repaired bodywork — get a panel/paint check",
      "Cambelt history unclear on a belt-driven engine",
    ],
  },
];

const FAQS = [
  {
    q: "Do you do pre-purchase inspections?",
    a: `We can run a paid diagnostic and visual inspection at the workshop — bring the car or arrange with the seller. Call ${NAP.phone} to discuss what's possible and price.`,
  },
  {
    q: "Can you check bodywork repairs?",
    a: "Yes. We can spot filler, paint mismatch, replaced panels and uneven gaps — call us before you commit.",
  },
];

export const Route = createFileRoute("/resources/used-car-viewing-checklist")({
  head: () =>
    seoHead({
      path: "/resources/used-car-viewing-checklist",
      title: "Free Used Car Viewing Checklist | What to Check Before Buying",
      description:
        "Free used car viewing checklist from Woodseer Autos: MOT history, warning lights, tyres, brakes, bodywork, test drive signs and service records.",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Free Resources", path: "/free-car-resources" },
        { name: "Used Car Viewing Checklist", path: "/resources/used-car-viewing-checklist" },
      ],
      jsonLd: [faqSchema(FAQS) as Record<string, unknown>],
    }),
  component: UsedCarChecklistResource,
});

function UsedCarChecklistResource() {
  const biz = useBusinessConfig();
  return (
    <>
      <section className="bg-foreground text-background py-14 md:py-20 no-print">
        <div className="mx-auto max-w-3xl px-4 md:px-6">
          <p className="text-[11px] font-bold tracking-[0.25em] uppercase text-primary">Free resource</p>
          <h1 className="mt-3 text-3xl md:text-4xl font-extrabold tracking-tight">
            Used Car Viewing Checklist
          </h1>
          <p className="mt-4 text-base text-background/70 leading-relaxed max-w-2xl">
            What to check before, during and after viewing a used car — written by a working garage that
            sees the consequences of skipped checks every week.
          </p>
          <a
            href={biz.phoneHref}
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
          >
            <Phone className="h-4 w-4" /> Call {biz.phone}
          </a>
        </div>
      </section>

      <div className="print-only px-6 pt-6 text-foreground">
        <p className="text-xl font-extrabold">Woodseer Autos — Used Car Viewing Checklist</p>
        <p className="text-sm">{NAP.phone} · woodseerautos.co.uk · 14 Copenhagen Place, London E14 7DF</p>
      </div>

      <section className="py-12 md:py-16">
        <div className="mx-auto max-w-3xl px-4 md:px-6 space-y-5">
          {SECTIONS.map((s) => (
            <div key={s.title} className="rounded-xl border border-border bg-card p-5 md:p-6">
              <h2 className="text-base md:text-lg font-bold text-foreground">{s.title}</h2>
              <ul className="mt-3 space-y-2">
                {s.items.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-foreground leading-relaxed">
                    <span
                      aria-hidden
                      className="mt-0.5 inline-block h-4 w-4 shrink-0 rounded-sm border-2 border-foreground/40"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div className="rounded-xl border border-border bg-secondary/40 p-5 md:p-6 text-sm text-foreground">
            <p className="font-semibold">Thinking of buying or selling?</p>
            <p className="mt-2 text-muted-foreground">
              Woodseer Autos can help with MOT, diagnostics, bodywork and repair checks.
            </p>
            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-primary">
              <Link to="/diagnostics" className="font-semibold hover:underline">Diagnostics →</Link>
              <Link to="/bodywork" className="font-semibold hover:underline">Bodywork →</Link>
              <Link to="/mot" className="font-semibold hover:underline">MOT →</Link>
              <Link to="/servicing" className="font-semibold hover:underline">Servicing →</Link>
              <Link to="/guide/$slug" params={{ slug: "selling-car-repair-checklist" }} className="font-semibold hover:underline">Selling-car checklist →</Link>
              <Link to="/premium-gallery" className="font-semibold hover:underline">Recent work →</Link>
              <Link to="/contact" className="font-semibold hover:underline">Contact →</Link>
            </div>
          </div>
        </div>
      </section>

      <FAQAccordion faqs={FAQS} title="Used car checks — FAQs" />
      <ResourceShareBlock path="/resources/used-car-viewing-checklist" title="Used Car Viewing Checklist" />
      <CTABlock headline="Want a second opinion before you buy?" subtext="Call us before you hand over the cash — honest, plain English." />
    </>
  );
}
